import { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { IAvailabilityState, IThunkAPI } from '@/store/types';
import type { IGetAvailabilityAction, ISetAvailabilityRequest } from '@/api/types';
import type {
  IAvailability,
  IIntervals,
  IParsedItem,
  ISelectedAvailability,
  IUpdateInterval,
  IUpdateResizeData,
} from '@/components/agent/my-availability/types';
import type { ScheduleId } from '@/types/schedule';

import { contract as api } from '@/api';
import {
  getOffsetData,
  initialParsedItem,
  isActionFromSlice,
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
  logError,
  makeCancellable,
  onFulfilledAction,
  onPendingAction,
  onRejectedAction,
} from '@/helpers';
import { SLICES } from '@/constants/actions';
import { parseAvailabilityData } from '@/helpers/availability/availability';

const initialState: IAvailabilityState = {
  availabilityList: null,
  availabilityBasket: [],
  parsedAvailabilityData: [],
  lastAvailabilityList: [],
  lastParsedData: [],

  meta: {
    pending: [],
  },
};

export const getAvailability = createAsyncThunk<
  { data: IAvailability[]; timezone: string; weekId: number; selectedSchedulesIds: ScheduleId[] },
  IGetAvailabilityAction,
  IThunkAPI
>('schedule/availability/get', async (payload, thunkAPI) => {
  try {
    const getAvailability = makeCancellable(api.getAvailability, thunkAPI.signal);
    const response = await getAvailability({ startBlock: payload.startBlock, endBlock: payload.endBlock });
    const { data } = response;
    if (typeof data === 'string') throw data;
    return {
      data,
      weekId: payload.weekId,
      timezone: payload.timezone,
      selectedSchedulesIds: payload.selectedSchedulesIds,
    };
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response || e.message);
  }
});

export const setAvailability = createAsyncThunk<IAvailability[], ISetAvailabilityRequest, IThunkAPI>(
  'schedule/availability/set',
  async (payload, thunkAPI) => {
    try {
      const response = await api.setAvailability(payload);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response || e.message);
    }
  },
);

export const slice = createSlice({
  name: 'availability',
  initialState,
  reducers: {
    clearAvailabilityList(state) {
      state.availabilityList = null;
      state.parsedAvailabilityData = [];
    },
    clearState(state) {
      state.meta.pending = [];
    },
    addNewInterval(
      state,
      action: PayloadAction<{ scheduleId: ScheduleId; newInterval: IIntervals; timeZone: string }>,
    ) {
      const { newInterval, scheduleId, timeZone } = action.payload;
      const { intervals, parsedData } = state.parsedAvailabilityData.find(item => item.scheduleId === scheduleId) || {};
      if (!intervals || !parsedData) return;
      intervals.push(newInterval);
      intervals.sort((a, b) => a.startOffset - b.startOffset || a.endOffset - b.endOffset);
      parsedData.push({
        ...initialParsedItem,
        ...getOffsetData(newInterval.startOffset, newInterval.endOffset, timeZone),
        isNew: true,
      });
      parsedData.sort(
        (a, b) =>
          a.startHour.weekdayIndex - b.startHour.weekdayIndex ||
          a.minutesFromMidnight - b.minutesFromMidnight ||
          a.blockDuration - b.blockDuration,
      );
    },
    deleteInterval(state, action: PayloadAction<{ scheduleId: ScheduleId; parsedItemIndex: number }>) {
      const { scheduleId, parsedItemIndex } = action.payload;
      const parsedDataBySchedule = state.parsedAvailabilityData.find(item => item.scheduleId === scheduleId);
      if (!parsedDataBySchedule) return;
      parsedDataBySchedule.intervals.splice(parsedItemIndex, 1);
      parsedDataBySchedule.parsedData.splice(parsedItemIndex, 1);
    },
    markToDelete(state, action: PayloadAction<{ scheduleId: ScheduleId; parsedItemIndex: number; status: boolean }>) {
      const { scheduleId, parsedItemIndex, status } = action.payload;
      const dataBySchedule = state.parsedAvailabilityData.find(item => item.scheduleId === scheduleId);

      if (dataBySchedule) {
        dataBySchedule.intervals[parsedItemIndex].toBeDeleted = status;
        dataBySchedule.parsedData[parsedItemIndex].toBeDeleted = status;

        slice.caseReducers.toggleModified(state, {
          type: 'availability/toggleModified',
          payload: { scheduleId, parsedItemIndex },
        });
      }
    },
    saveBasket(state, action: PayloadAction<ISelectedAvailability[]>) {
      state.availabilityBasket = action.payload;
    },
    resetChangedAvailability(state, action: PayloadAction<ISelectedAvailability['basketData']>) {
      action.payload.forEach(({ parsedItemIndex, scheduleId }) => {
        const parsedDataBySchedule = state.parsedAvailabilityData.find(item => item.scheduleId === scheduleId);
        const lastParsedDataBySchedule = state.lastParsedData.find(item => item.scheduleId === scheduleId);

        if (!parsedDataBySchedule || !lastParsedDataBySchedule) return;

        if (parsedDataBySchedule?.intervals[parsedItemIndex].isNew === true) {
          slice.caseReducers.deleteInterval(state, {
            type: 'availability/deleteInterval',
            payload: { parsedItemIndex, scheduleId },
          });
        } else {
          parsedDataBySchedule.intervals[parsedItemIndex] = lastParsedDataBySchedule.intervals[parsedItemIndex];
          parsedDataBySchedule.parsedData[parsedItemIndex] = lastParsedDataBySchedule.parsedData[parsedItemIndex];
        }
      });
    },
    resetAllBasketChanges(state) {
      state.parsedAvailabilityData = state.lastParsedData;
    },
    toggleModified(state, action: PayloadAction<{ scheduleId: ScheduleId; parsedItemIndex: number }>) {
      const { scheduleId, parsedItemIndex } = action.payload;
      const parsedDataBySchedule = state.parsedAvailabilityData.find(item => item.scheduleId === scheduleId);
      const lastParsedDataBySchedule = state.lastParsedData.find(item => item.scheduleId === scheduleId);

      if (!parsedDataBySchedule) return;

      const interval = parsedDataBySchedule.intervals[parsedItemIndex];
      const oldInterval = lastParsedDataBySchedule?.intervals[parsedItemIndex];
      const parsedItem = parsedDataBySchedule.parsedData[parsedItemIndex];

      const isModified = !Boolean(
        interval.startOffset === oldInterval?.startOffset &&
          interval.endOffset === oldInterval?.endOffset &&
          interval.colSpan === oldInterval.colSpan &&
          interval.colOffset === oldInterval.colOffset,
      );
      interval.isModified = isModified && interval.isNew === false && interval.toBeDeleted === false;
      parsedItem.isModified = isModified && parsedItem.isNew === false && parsedItem.toBeDeleted === false;
    },
    updateResizeData(state, action: PayloadAction<IUpdateResizeData>) {
      const parsedItem = state.parsedAvailabilityData
        .find(item => item.scheduleId === action.payload.scheduleId)
        ?.parsedData.filter(item => item.weekday.slice(0, 3) === action.payload.dayName)
        .find((_, index) => index === action.payload.parsedItemIndex);

      Object.assign(parsedItem, {
        ...parsedItem,
        resizeData: { ...action.payload.resizeData },
      });
    },
    updateInterval(state, action: PayloadAction<IUpdateInterval & { parsedItemToUpdate: IParsedItem }>) {
      const { scheduleId, intervalToUpdate, intervalIndex, parsedItemToUpdate } = action.payload;
      const dataBySchedule = state.parsedAvailabilityData.find(item => item.scheduleId === scheduleId);

      if (dataBySchedule) {
        dataBySchedule.intervals[intervalIndex] = intervalToUpdate;
        dataBySchedule.parsedData[intervalIndex] = {
          ...parsedItemToUpdate,
          resizeData: {
            ...parsedItemToUpdate.resizeData,
            topOffset: 0,
            resizeOffset: 0,
            leftOffset: undefined,
          },
        };

        slice.caseReducers.toggleModified(state, {
          type: 'availability/toggleModified',
          payload: { scheduleId, parsedItemIndex: intervalIndex },
        });
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getAvailability.fulfilled, (state, { payload }) => {
        if (state.availabilityList) state.lastAvailabilityList = state.availabilityList;

        state.availabilityList = payload.data
          .map(item => ({
            ...item,
            intervals: item.intervals.map(interval => ({
              ...interval,
              colSpan: 1,
              colOffset: 0,
              toBeDeleted: false,
              isModified: false,
              isNew: false,
            })),
          }))
          .sort((a, b) => a.scheduleId - b.scheduleId);

        const parsedData = parseAvailabilityData(
          state.availabilityList,
          state.lastAvailabilityList,
          state.lastParsedData,
          payload.selectedSchedulesIds,
          payload.timezone,
        );

        state.parsedAvailabilityData = parsedData;
        state.lastParsedData = state.parsedAvailabilityData;
      })
      .addCase(setAvailability.fulfilled, state => {
        state.availabilityBasket = [];
      })
      .addMatcher(
        action => isFulfilledAction(action) && isActionFromSlice(action, SLICES.AVAILABILITY),
        onFulfilledAction,
      )
      .addMatcher(
        action => isRejectedAction(action) && isActionFromSlice(action, SLICES.AVAILABILITY),
        onRejectedAction,
      )
      .addMatcher(action => isPendingAction(action) && isActionFromSlice(action, SLICES.AVAILABILITY), onPendingAction);
  },
});

// actions
export const {
  clearState,
  clearAvailabilityList,
  addNewInterval,
  deleteInterval,
  markToDelete,
  saveBasket,
  resetChangedAvailability,
  resetAllBasketChanges,
  updateInterval,
  updateResizeData,
} = slice.actions;

export default slice.reducer;
